var kind      = require("enyo/kind"),
    Component = require("enyo/Component"),
    raf       = require("enyo/animation"),
    utils     = require("enyo/utils");

/**
 * [AnimationLoop]{@link enyo-anim.AnimationLoop} allows registering multiple
 * callbacks to a single animationFrame.
 *
 * @class   enyo-anim.AnimationLoop
 * @extends enyo.Component
 * @public
 */
module.exports = kind.singleton(/** @lends enyo-anim.AnimationLoop.prototype */ {

    /**
     * @private
     */
    name: "enyo-anim.AnimationLoop",

    /**
     * @private
     */
    kind: Component,

    /**
     * A hash of named callbacks.
     *
     * @type {Object}
     * @private
     */
    callbacks: null,

    /**
     * The amount of registered callbacks.
     *
     * @type {number}
     * @private
     */
    length: null,

    /**
     * The id of the registered animation frame.
     *
     * @type {?number}
     * @private
     */
    frame: null,

    /**
     * A millisecond timestamp taken at the start of the frame. Use the
     * difference between it and `enyo.utils.perfNow()` to determine how long
     * the current frame has been executing.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    frameTime: null,

    /**
     * The time spent executing the latest tick.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    lastFrameDuration: 0,

    /**
     * Sets up a hash to store callbacks.
     *
     * @memberof enyo-anim.AnimationLoop
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            this.euid      = this.euid || utils.uid();
            this.callbacks = {};
            this.length    = 0;
            sup.apply(this, arguments);
        };
    }),

    /**
     * @memberof enyo-anim.AnimationLoop
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.tick = this.bindSafely(this.tick);
            sup.apply(this, arguments);
        };
    }),

    /**
     * Request a callback to be executed on the next animationFrame.
     * Automatically schedules an animationFrame if none is scheduled yet.
     *
     * @param {string}    name      The name
     * @param {Function}  callback  The callback
     * @public
     */
    request: function(name, callback) {
        if (!this.isScheduled(name)) {
            ++this.length;
        }

        this.callbacks[name] = callback;

        if (!this.frame) {
            this.frame = raf.requestAnimationFrame(this.tick);
        }
    },

    /**
     * Determines if the named callback is scheduled to be executed on the next
     * animationFrame.
     *
     * @param  {string}   name  The name
     * @return {boolean}  `true` if scheduled, `false` otherwise.
     * @public
     */
    isScheduled: function(name) {
        return Boolean(this.callbacks[name]);
    },

    /**
     * Removes the callback.
     *
     * @param {string}  name  The name
     * @public
     */
    cancel: function(name) {
        if (this.isScheduled(name)) {
            --this.length;
        }

        delete this.callbacks[name];

        if (this.length === 0) {
            raf.cancelAnimationFrame(this.frame);
            this.frame = null;
        }
    },

    /**
     * Executes all callbacks.
     *
     * @private
     */
    tick: function() {
        var callbacks = this.callbacks,
            name,
            callback;

        this.frame     = null;
        this.frameTime = utils.perfNow();

        for (name in callbacks) {
            if (callbacks.hasOwnProperty(name)) {
                callback = callbacks[name];
                delete callbacks[name];
                --this.length;
                callback();
            }
        }

        this.lastFrameDuration = utils.perfNow() - this.frameTime;
    }

});
